import { simpleObjectHash } from '@/features/shared/tools/object'

export function withCache<TArgs extends readonly unknown[], TReturn>(
  func: (...args: TArgs) => TReturn,
  options?: { cacheSize: number },
): typeof func {
  type CacheItem = {
    key: string
    value: TReturn
  }
  const cache: CacheItem[] = []

  return (...args: TArgs) => {
    // Calcule une clée pour les paramétres
    const key = simpleObjectHash(args)

    // Recherche le résultat
    let result = cache.find((item) => item.key === key)
    if (result) return result.value

    // Execute func si absent
    result = {
      key,
      value: func(...args),
    }

    //Ajoute au cache en première position
    cache.unshift(result)

    // Si nb éléments > cacheSize, retire le dernier élément
    if (options?.cacheSize && cache.length > options.cacheSize) {
      cache.pop()
    }

    return result.value
  }
}

/**
 * Exécute une fonction de façon sécurisée (sans lever d'exception).
 *
 * - Si `fn` est `undefined`, la fonction ne fait rien et retourne `undefined`.
 * - Si `fn` s'exécute correctement, son résultat est retourné.
 * - Si `fn` lève une exception, l'erreur est interceptée, journalisée via `console.error`,
 *   puis la fonction retourne `undefined`.
 *
 * @typeParam T - Type de la valeur retournée par `fn`.
 * @param fn - Fonction à exécuter (optionnelle).
 * @returns Le résultat de `fn` si tout se passe bien, sinon `undefined`.
 *
 * @example
 * const value = safeCall(() => JSON.parse('{"ok": true}'))
 * // value -> { ok: true }
 *
 * @example
 * const value = safeCall(() => JSON.parse('invalid-json'))
 * // console.error(...)
 * // value -> undefined
 */
export function safeCall<T>(fn: (() => T) | undefined): T | undefined {
  try {
    return fn?.()
  } catch (error) {
    console.error(error)
  }
}
